import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';

export default function WebsiteOrderSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState('checking');
  const [ref, setRef] = useState('');
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState('error'); return undefined; }
    let stop = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API_URL}/api/website/checkout-status/${sessionId}`, { credentials: 'include' });
        const d = await r.json();
        if (stop) return;
        if (d.payment_status === 'paid' && d.order_status && d.order_status !== 'draft') { setRef(d.reference || ''); setState('paid'); return; }
        if (['expired', 'failed'].includes(d.payment_status)) { setState('error'); return; }
      } catch { /* retry */ }
      tries.current += 1;
      if (tries.current < 10) setTimeout(poll, 2000); else setState('timeout');
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#E8ECEF] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="website-order-success">
        {state === 'checking' && <><Loader2 className="w-12 h-12 text-teal-700 animate-spin mx-auto" /><h1 className="text-xl font-bold mt-4">Confirming your payment…</h1><p className="text-sm text-slate-500 mt-1">This only takes a moment.</p></>}
        {state === 'paid' && <><CheckCircle2 className="w-14 h-14 text-teal-700 mx-auto" /><h1 className="text-2xl font-bold mt-4" data-testid="wos-title">Order received!</h1>
          <p className="text-sm text-slate-600 mt-1">Your website order <strong data-testid="wos-ref">{ref}</strong> is with the Ox Resin team. We'll email and text you as it moves along — you can track it under My Website.</p>
          <Button className="mt-6 w-full h-11 bg-teal-700 text-white" onClick={() => navigate('/my-website')} data-testid="wos-track">Track my website</Button></>}
        {(state === 'timeout' || state === 'error') && <><AlertTriangle className="w-12 h-12 text-amber-500 mx-auto" /><h1 className="text-xl font-bold mt-4">{state === 'timeout' ? 'Still processing' : 'Payment not completed'}</h1>
          <p className="text-sm text-slate-600 mt-1">{state === 'timeout' ? "Your bank is taking a little longer. Check My Website in a minute — you won't be charged twice." : 'No charge was made. Your answers are saved so you can try again.'}</p>
          <Button className="mt-6 bg-slate-900 text-white" onClick={() => navigate('/my-website')} data-testid="wos-back">Back to My Website</Button></>}
      </div>
    </div>
  );
}
